import React, { Component, Fragment } from 'react';

import { Loader } from 'components/loader';
import { SURVEY_EVENTS, invokeEvent } from 'configs/Survicate';
import { DataHubPlayerService } from 'modules/app/services/datahub/player';
import MembershipService from 'modules/membership/MembershipService';
import { PropTypes } from 'prop-types';

import ConfigDRM from './ConfigDrm';
import ConfigStreaming from './ConfigStreaming';
import ConfirmationModal from './ConfirmationModal';
import ConfirmationModalInfo from './ConfirmationModalInfo';
import { streamingCondition as streamingType } from './constants';
import ActivateModal from './drm/ActivateModal';
import DeactivateModal from './drm/DeactivateModal';
import StreamingEvents from './events';

import { UiService } from '../../ui';
import { ALERT } from '../../ui/constants';
import ConfigService from '../ConfigService';

import './ConfigStreaming.scss';

export default class ConfigStreamingManager extends Component {
  static propTypes = {
    fixedBarId: PropTypes.string.isRequired,
    tabIndex: PropTypes.number.isRequired,
    showDrmConfig: PropTypes.bool,
    isTabActivated: PropTypes.bool
  };

  static defaultProps = {
    showDrmConfig: false
  };

  state = {
    streamingActive: false,
    streamingCondition: streamingType.DEFAULT,
    addOnCost: null,
    bundleAddOnCost: null,
    promotionalCost: null,
    streamingModal: false,
    loading: true,
    drmActive: false,
    drmModal: false,
    activatingDrm: false
  };

  async componentDidMount() {
    await this.setUp();
    this.disableLoader();
  }

  componentDidUpdate() {
    if (this.props.isTabActivated) {
      if (this.state.streamingActive) {
        DataHubPlayerService.streamingEnabledOnContentPageLoadEvent();
      } else {
        DataHubPlayerService.streamingDisabledOnContentPageLoadEvent();
      }
    }
  }

  async setUp() {
    await Promise.all([
      this.getInitialData(),
      this.getStreamingCosts(),
      this.getBundleStreamingCosts()
    ]);
    this.resolveStreamingCondition();
  }

  resolveStreamingCondition() {
    this.setState({
      streamingCondition: ConfigService.resolveStreamingCondition()
    });
  }

  async getStreamingCosts() {
    const costs = await ConfigService.getStreamingCosts();

    this.setState({
      addOnCost: costs?.addOnCost,
      promotionalCost: costs?.promotionalCost
    });
  }

  async getBundleStreamingCosts() {
    const bundleAddOnCost = await ConfigService.getBundleStreamingCosts();

    this.setState({
      bundleAddOnCost
    });
  }

  async getInitialData() {
    const { streamingActive, drmProtectionActive: drmActive } =
      await ConfigService.getStreamingStatus();

    this.setState({ streamingActive, drmActive });
  }

  saveStreamingActive = () => {
    const { streamingActive } = this.state;

    this.setState({ streamingActive: !streamingActive }, async () => {
      try {
        await ConfigService.updateStreamingStatus({ active: !streamingActive });
        UiService.setAlertSuccess('done');

        if (!streamingActive) {
          DataHubPlayerService.changePlayerEnableEvent();
        } else {
          DataHubPlayerService.changePlayerDisableEvent();
          invokeEvent(SURVEY_EVENTS.PLAYER_CHURN);
        }
      } catch {
        if (streamingActive) {
          DataHubPlayerService.errorOnDeactivatePlayerInProductSettingsEvent();
        } else {
          DataHubPlayerService.errorOnActivatePlayerInProductSettingsEvent();
        }

        this.setState({ streamingActive });
      }

      this.closeStreamingModal();
    });
  };

  handleStreamingActiveChange = () => {
    const { streamingActive } = this.state;
    const { isOnlineEventTemplate } = MembershipService;

    if (streamingActive && !isOnlineEventTemplate) {
      this.showStreamingModal();
      DataHubPlayerService.openDeactivatePlayerModalEvent();
    } else {
      this.saveStreamingActive();
    }
  };

  saveDrmActive = () => {
    const { drmActive } = this.state;

    this.closeDrmModal();

    this.setState({ drmActive: !drmActive }, async () => {
      try {
        await ConfigService.updateDrmStatus(!drmActive);
        this.setState({ activatingDrm: !drmActive });
        if (drmActive) {
          UiService.setAlertSuccess('done');
          StreamingEvents.sendConfirmDeactivateEvent();
        } else {
          UiService.setAlertInfo('activating_drm', {}, ALERT.TIMEOUT.LONG);
          StreamingEvents.sendConfirmActivateEvent();
        }
      } catch {
        this.setState({ drmActive });
      }
    });
  };

  handleDrmActiveChange = () => {
    const { drmActive } = this.state;

    if (drmActive) {
      StreamingEvents.sendDeactivateEvent();
    } else {
      StreamingEvents.sendActivateEvent();
    }

    this.openDrmModal();
  };

  disableLoader = () => this.setState({ loading: false });

  closeStreamingModal = () => {
    this.setState({ streamingModal: false });
  };

  handleCancelConfirmationStreaming = () => {
    DataHubPlayerService.cancelPlayerDeactivationOnProductSettingsEvent();

    this.closeStreamingModal();
  };

  showStreamingModal = () => {
    this.setState({ streamingModal: true });
  };

  openDrmModal = () => {
    this.setState({ drmModal: true });
  };

  closeDrmModal = () => {
    this.setState({ drmModal: false });
  };

  renderDrmModal = () => {
    const { drmActive, drmModal } = this.state;

    return (
      <>
        <ActivateModal
          isOpen={drmModal && !drmActive}
          onConfirm={this.saveDrmActive}
          onCancel={this.closeDrmModal}
        />
        <DeactivateModal
          isOpen={drmModal && drmActive}
          onConfirm={this.saveDrmActive}
          onCancel={this.closeDrmModal}
        />
      </>
    );
  };

  get confirmationInfo() {
    const { isMidnight } = MembershipService;
    const { drmActive } = this.state;

    const confirmationInfo = [
      {
        label:
          'config_manager.config_streaming.deactivate_streaming.videos_not_visible',
        icon: 'video-slash'
      }
    ];

    if (isMidnight) {
      confirmationInfo.push({
        label:
          'config_manager.config_streaming.deactivate_streaming.default_template',
        icon: 'file-invoice'
      });
    }

    if (drmActive) {
      confirmationInfo.push({
        label: 'config_manager.config_streaming.deactivate_streaming.no_drm',
        icon: 'exclamation-triangle'
      });
    } else {
      confirmationInfo.push({
        label:
          'config_manager.config_streaming.deactivate_streaming.no_antifraud',
        icon: 'exclamation-triangle'
      });
    }

    return confirmationInfo;
  }

  render() {
    const {
      streamingActive,
      streamingModal,
      streamingCondition,
      addOnCost,
      bundleAddOnCost,
      promotionalCost,
      loading,
      drmActive,
      activatingDrm
    } = this.state;
    const { tabIndex, showDrmConfig } = this.props;

    return (
      <Fragment>
        {loading && <Loader fixed />}

        <ConfigStreaming
          streamingActive={streamingActive}
          streamingCondition={streamingCondition}
          addOnCost={addOnCost}
          bundleAddOnCost={bundleAddOnCost}
          promotionalCost={promotionalCost}
          tabIndex={tabIndex}
          onToggleChange={this.handleStreamingActiveChange}
        />

        {streamingModal && (
          <ConfirmationModal
            title="config_manager.config_streaming.deactivate_streaming.title"
            isOpen={streamingModal}
            onCancel={this.handleCancelConfirmationStreaming}
            onConfirm={this.saveStreamingActive}
            confirmLabel="config_manager.config_streaming.deactivate_streaming.confirm"
          >
            {this.confirmationInfo.map((info) => (
              <ConfirmationModalInfo key={info.label} {...info} />
            ))}
          </ConfirmationModal>
        )}

        {showDrmConfig && (
          <>
            <ConfigDRM
              active={drmActive}
              onChange={this.handleDrmActiveChange}
              streamingActive={streamingActive}
              activatingDrm={activatingDrm}
            />
            {this.renderDrmModal()}
          </>
        )}
      </Fragment>
    );
  }
}
